import { isApolloError } from "@apollo/client";
import { HttpError, NetworkError } from "~/api-fetch/apiFetch";
import { isExpired } from "~/Auth/jwtUtils";
import { getRequestMetadata } from "./requestMetadata";

/** What telemetry needs to say about a failed API request. */
export type FailureDetails = {
  operation: string;
  endpoint?: string;
  url?: string;
  status?: number;
  tokenExpired?: boolean;
  unparseable?: boolean;
  callStack?: string;
};

export type Classification =
  /** The request never reached the server. Outside our control; not reported. */
  | { kind: "network" }
  /** Recorded by backend telemetry; not reported. */
  | { kind: "server-500" }
  /** Possibly gateway-originated, so counted, but not a frontend error. */
  | { kind: "server-5xx"; details: FailureDetails }
  /** The expected race at session expiry; not reported. */
  | { kind: "unauthorized-expired" }
  | { kind: "reportable"; details?: FailureDetails };

// The shape of Apollo's ServerError / ServerParseError network errors.
type ApolloNetworkError = Error & { statusCode?: number };

const isFetchRejection = (error: Error) =>
  error.name === "TypeError" || error.name === "AbortError";

function classifyByStatus(details: FailureDetails): Classification {
  const { status } = details;

  if (status === 500) {
    return { kind: "server-500" };
  }

  if (status !== undefined && status > 500 && status < 600) {
    return { kind: "server-5xx", details };
  }

  // 401 is deliberately reported when the token sent with the request was
  // still live. Suppressing every 401 once hid a month-long regression:
  // abandoned tabs replayed expired tokens indefinitely and the only visible
  // symptom was blank screens. A server rejecting a token the client believes
  // is valid (clock skew, an auth middleware fault, a bad deploy) is a real
  // fault. A 401 against a token that has expired by the time the response
  // arrives — including one that expired in flight — is the expected race at
  // session expiry, which session teardown already handles.
  if (status === 401 && details.tokenExpired === true) {
    return { kind: "unauthorized-expired" };
  }

  return { kind: "reportable", details };
}

/**
 * Decides how an error is treated by telemetry, from the structure of the
 * failure — never from browser-specific message text.
 *
 * `now` is when the failure is being classified; a 401 is judged against the
 * sent token's expiry at this moment.
 */
export function classifyError(
  error: unknown,
  now: number = Date.now()
): Classification {
  if (error instanceof NetworkError) {
    return { kind: "network" };
  }

  if (error instanceof HttpError) {
    return classifyByStatus({
      operation: error.operation,
      url: error.url,
      status: error.status,
      tokenExpired: isExpired(error.sentToken ?? "", now),
      callStack: error.callStack,
    });
  }

  if (error instanceof Error && isApolloError(error) && error.networkError) {
    const networkError = error.networkError as ApolloNetworkError;
    const status =
      typeof networkError.statusCode === "number"
        ? networkError.statusCode
        : undefined;

    if (status === undefined && isFetchRejection(networkError)) {
      return { kind: "network" };
    }

    const metadata = getRequestMetadata(networkError);
    const details: FailureDetails = {
      operation: metadata?.operation ?? "unknown",
      endpoint: metadata?.endpoint,
      status,
      // Without metadata the sent token is unknown, so a 401 is reported
      // rather than assumed to be an expiry race.
      tokenExpired: metadata
        ? isExpired(metadata.sentToken ?? "", now)
        : undefined,
    };

    if (networkError.name === "ServerParseError") {
      return { kind: "reportable", details: { ...details, unparseable: true } };
    }

    return classifyByStatus(details);
  }

  return { kind: "reportable" };
}
